import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProjectProvider } from './contexts/ProjectContext';
import NotificationManager from './components/NotificationManager';
import GlobalTaskMonitor from './components/GlobalTaskMonitor';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Hosts from './pages/Hosts';
import Graph from './pages/Graph';
import Tasks from './pages/Tasks';
import Scan from './pages/UniversalScan';
import Diff from './pages/Diff';
import Config from './pages/Config';
import Vulnerabilities from './pages/Vulnerabilities';
import Projects from './pages/Projects';
import Admin from './pages/Admin';
import Invite from './pages/Invite';
import Wiki from './pages/Wiki';
import AutoDiscovery from './pages/AutoDiscovery';
import FileManager from './pages/FileManager';
import Healthcheck from './pages/Healthcheck';
import DataFeeds from './pages/DataFeeds';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const savedUsername = localStorage.getItem('username');
    
    if (token && savedUsername) {
      setIsAuthenticated(true);
      setUsername(savedUsername);
    }
    
    setLoading(false);
  }, []);

  const handleLogin = (user) => {
    setIsAuthenticated(true);
    setUsername(user);
  };

  const handleLogout = async () => {
    // Best-effort server-side revocation so the refresh token dies too,
    // not just the locally-stored copy. Never block logout on this.
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        await fetch(`${apiUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (e) {
      // ignore network/auth errors — proceed to clear local session anyway
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
    setUsername('');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider>
        <Login onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <ProjectProvider>
        <NotificationManager>
          <Router>
            <GlobalTaskMonitor />
            <div style={{ display: 'flex', minHeight: '100vh' }}>
              <Sidebar onLogout={handleLogout} username={username} />
              
              <div style={{
                flex: 1,
                marginLeft: '250px',
                backgroundColor: 'var(--bg-primary)',
                minHeight: '100vh',
              }}>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/hosts" element={<Hosts />} />
                  <Route path="/graph" element={<Graph />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/scan" element={<Scan />} />
                  <Route path="/vulnerabilities" element={<Vulnerabilities />} />
                  <Route path="/vulnerabilities/:vulnId" element={<Vulnerabilities />} />
                  <Route path="/diff" element={<Diff />} />
                  <Route path="/config" element={<Config />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/invite/:inviteCode" element={<Invite />} />
                  <Route path="/wiki" element={<Wiki />} />
                  <Route path="/autodiscovery" element={<AutoDiscovery />} />
                  <Route path="/files" element={<FileManager />} />
                  <Route path="/healthcheck" element={<Healthcheck />} />
                  <Route path="/data-feeds" element={<DataFeeds />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </div>
          </Router>
        </NotificationManager>
      </ProjectProvider>
    </ThemeProvider>
  );
}

export default App;
